import type { MessageView } from "../types/ui";
import { isGifUrl, messageDisplayBody } from "./message-presentation";
import { renderForSurface } from "./secrets-placeholder";

export type ReplyPreview = NonNullable<MessageView["replyTo"]>;

/** Called after local decryption. Never fetch or duplicate plaintext on the server. */
export function previewReply(message: MessageView): ReplyPreview {
  const safe = renderForSurface({ kind: message.kind ?? "text", body: message.body, surface: "mention" });
  const base = { xid: message.xid, authorXid: message.authorXid, authorName: message.authorName, authorAvatarUrl: message.authorAvatarUrl };
  // Redaction must precede file/GIF detection, even for a secret with file metadata.
  if (safe.redacted) return { ...base, kind: "secret", preview: safe.display };
  if (message.file || message.kind === "file") return { ...base, kind: message.file?.mime.startsWith("image/") ? "image" : "file", preview: "Click to see attachment" };
  if (isGifUrl(safe.display.trim())) return { ...base, kind: "gif", preview: "Click to see GIF" };
  return { ...base, kind: "text", preview: messageDisplayBody(safe.display).replace(/\s+/g, " ").trim().slice(0, 140) || "Message" };
}

/** Recompute from the same chat snapshot so decrypts, edits and deletions stay in sync. */
export function resolveReplyPreviews(messages: MessageView[], redactAll = false): MessageView[] {
  const byId = new Map(messages.map(message => [message.xid, message]));
  return messages.map(message => {
    if (!message.replyTo) return message;
    const parent = byId.get(message.replyTo.xid);
    const replyTo: ReplyPreview = parent && parent !== message
      ? previewReply(redactAll ? { ...parent, kind: "secret" } : parent)
      : { xid: message.replyTo.xid, authorName: "", preview: "Original message isn’t loaded or is no longer available", unavailable: true };
    return { ...message, replyTo };
  });
}
